import { useState } from 'react';
import { Link } from 'react-router-dom';
import { resolveImagePath } from '@/utils/imageMap';
import { cn } from '@/utils/cn';

interface NavItem {
  label: string;
  href: string;
  children?: NavItem[];
}

const navigation: NavItem[] = [
  { label: 'HOME', href: '/' },
  { label: 'WORSHIP', href: '/worship' },
  { label: 'CONNECT', href: '/connect' },
  { label: 'DONATE', href: '/donate' },
  { label: 'ANNOUNCEMENTS', href: '/announcements' },
  {
    label: 'RESOURCES',
    href: '/resources',
    children: [
      { label: 'PLANNING EVENTS', href: '/planning-events' },
      { label: 'VENUE REQUEST', href: '/venue-request' },
      { label: 'PRE-MARITAL COUNSELING', href: '/pre-marital-counseling' },
    ]
  },
  { label: 'STAFF & VESTRY', href: '/staff-vestry' },
  { label: 'RECTOR SEARCH', href: '/rector-search' },
  { label: 'JOBS', href: '/jobs' },
];

interface LanguageSelectorProps {
  className?: string;
}

function LanguageSelector({ className }: LanguageSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className={cn("relative", className)}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-white bg-black bg-opacity-20 rounded hover:bg-opacity-30 transition-colors"
        aria-label="Select language"
      >
        <img
          src={resolveImagePath('/images/en-us-flag.svg')}
          alt="English"
          className="w-4 h-3"
        />
        <span>English</span>
        <svg
          className={cn("w-4 h-4 transition-transform", isOpen && "rotate-180")}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {isOpen && (
        <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded shadow-lg z-50">
          <button
            className="flex items-center gap-2 w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
            onClick={() => setIsOpen(false)}
          >
            <img
              src={resolveImagePath('/images/en-us-flag.svg')}
              alt="English"
              className="w-4 h-3"
            />
            <span>English</span>
          </button>
        </div>
      )}
    </div>
  );
}

interface NavigationMenuProps {
  className?: string;
}

function NavigationMenu({ className }: NavigationMenuProps) {
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);

  return (
    <nav className={cn("bg-white w-full", className)}>
      <ul className="flex items-center justify-center">
        {navigation.map((item) => (
          <li
            key={item.label}
            className="relative group"
            onMouseEnter={() => item.children && setOpenDropdown(item.label)}
            onMouseLeave={() => setOpenDropdown(null)}
          >
            <Link
              to={item.href}
              className={cn(
                "block px-4 py-4 text-base font-body font-normal text-trinity-burgundy hover:bg-trinity-burgundy hover:text-white transition-colors uppercase tracking-wide",
                item.label === 'HOME' && "bg-trinity-burgundy text-white"
              )}
            >
              {item.label}
              {item.children && (
                <svg
                  className="inline w-3 h-3 ml-1"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" />
                </svg>
              )}
            </Link>
            
            {item.children && openDropdown === item.label && (
              <div className="absolute top-full left-0 min-w-64 bg-white border border-gray-200 shadow-lg z-50">
                <ul className="py-2">
                  {item.children.map((child) => (
                    <li key={child.label}>
                      <Link
                        to={child.href}
                        className="block px-6 py-3 text-sm font-body text-trinity-burgundy hover:bg-trinity-burgundy hover:text-white transition-colors uppercase tracking-wide"
                      >
                        {child.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </li>
        ))}
      </ul>
    </nav>
  );
}

interface MobileMenuProps {
  isOpen: boolean;
  onClose: () => void;
}

function MobileMenu({ isOpen, onClose }: MobileMenuProps) {
  const [openSubmenu, setOpenSubmenu] = useState<string | null>(null);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 lg:hidden">
      <div className="bg-white w-80 h-full overflow-y-auto">
        <div className="p-4 border-b border-gray-200">
          <button
            onClick={onClose}
            className="float-right text-gray-500 hover:text-gray-700"
            aria-label="Close menu"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <h2 className="text-lg font-semibold text-trinity-burgundy">Menu</h2>
        </div>
        
        <nav className="p-4">
          <ul className="space-y-2">
            {navigation.map((item) => (
              <li key={item.label}>
                <div className="flex items-center justify-between">
                  <Link
                    to={item.href}
                    className="block py-2 text-trinity-burgundy font-medium hover:text-trinity-burgundy-dark"
                    onClick={onClose}
                  >
                    {item.label}
                  </Link>
                  {item.children && (
                    <button
                      onClick={() => setOpenSubmenu(openSubmenu === item.label ? null : item.label)}
                      className="p-1 text-trinity-burgundy"
                    >
                      <svg
                        className={cn("w-4 h-4 transition-transform", openSubmenu === item.label && "rotate-180")}
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" />
                      </svg>
                    </button>
                  )}
                </div>
                
                {item.children && openSubmenu === item.label && (
                  <ul className="ml-4 mt-2 space-y-1 border-l-2 border-trinity-burgundy pl-4">
                    {item.children.map((child) => (
                      <li key={child.label}>
                        <Link
                          to={child.href}
                          className="block py-1 text-sm text-gray-600 hover:text-trinity-burgundy"
                          onClick={onClose}
                        >
                          {child.label}
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            ))}
          </ul>
        </nav>
      </div>
    </div>
  );
}

export function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <header>
      {/* Language Selector and Logo Section */}
      <div className="bg-trinity-burgundy text-white">
        <div className="relative">
          {/* Language Selector - Top Left */}
          <div className="absolute top-4 left-4 z-10">
            <LanguageSelector />
          </div>
          
          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(true)}
            className="lg:hidden absolute top-4 right-4 z-10 p-2 text-white hover:text-gray-200"
            aria-label="Open menu"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>

          {/* Centered Logo and Church Info */}
          <div className="flex items-center justify-center py-8 px-4">
            <Link to="/" className="flex items-center space-x-6">
              <img
                src={resolveImagePath('/images/trinity-logo.png')}
                alt="Trinity Episcopal Church Tulsa"
                className="w-16 h-16 object-contain"
              />
              <div className="text-left">
                <h1 className="text-4xl lg:text-5xl font-heading font-normal text-white leading-tight mb-2">
                  Trinity Episcopal Church
                </h1>
                <h2 className="text-lg lg:text-xl font-heading font-normal text-white tracking-wider uppercase">
                  TULSA, OKLAHOMA
                </h2>
              </div>
            </Link>
          </div>
        </div>
      </div>

      {/* Desktop Navigation */}
      <NavigationMenu className="hidden lg:block" />

      {/* Mobile Menu */}
      <MobileMenu 
        isOpen={mobileMenuOpen} 
        onClose={() => setMobileMenuOpen(false)} 
      />
    </header>
  );
}